import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSession } from "@/context/SessionContext";

const HERO = "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?auto=format&fit=crop&w=1600&q=80";

const STEPS = [
  ["01", "Tell us the space", "Home, office or a commercial build — size, rooms, who lives there."],
  ["02", "Scroll & save", "Hundreds of interiors. Tap what moves you. We read the pattern."],
  ["03", "Your board, ready", "A palette, a furniture taste list and a brief any designer can build from."],
];

export default function Landing() {
  const navigate = useNavigate();
  const { session, start, reset } = useSession();

  const begin = async () => {
    await start();
    navigate("/project");
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <span className="font-display text-2xl sm:text-3xl tracking-wide">interiorsmoodboard</span>
          <Link to="/admin" data-testid="landing-admin-link" className="text-xs tracking-[0.2em] uppercase text-neutral-400 hover:text-neutral-900 transition-colors">Studio</Link>
        </div>
      </header>

      <section className="relative min-h-screen grid lg:grid-cols-12">
        <div className="lg:col-span-7 flex flex-col justify-center px-6 sm:px-10 lg:pl-20 pt-32 pb-16 lg:py-0">
          <p className="eyebrow rise">Interior taste finder</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.98] tracking-tight mt-6 rise d1">
            Let's help you choose<br />the right interiors<br /><span className="italic">for your space.</span>
          </h1>
          <p className="text-neutral-500 text-base sm:text-lg leading-relaxed max-w-md mt-8 rise d2">
            A house, an office or a commercial project. Scroll, save what you love, and leave with a board that tells any designer exactly what you want.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-12 rise d3">
            <button data-testid="hero-start-button" onClick={begin} className="btn-ink h-14 px-9 text-base">
              Start choosing <ArrowRight size={18} />
            </button>
            {session && (
              <button data-testid="hero-resume-button" onClick={() => navigate(session.step >= 5 ? "/board" : ["/project", "/styles", "/palette", "/about", "/furniture", "/board"][session.step] || "/project")} className="btn-ghost h-14">
                Resume where I left off
              </button>
            )}
          </div>
          {session && (
            <button data-testid="hero-reset-button" onClick={reset} className="text-xs text-neutral-400 underline underline-offset-4 mt-4 w-fit rise d4">Start a fresh board instead</button>
          )}
        </div>
        <div className="lg:col-span-5 relative min-h-[50vh] lg:min-h-screen">
          <img src={HERO} alt="A quiet, light-filled living room" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute bottom-6 left-6 bg-white/85 backdrop-blur px-4 py-2 rounded-full text-xs tracking-wider">Made on interiorsmoodboard</div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 lg:py-32 grid md:grid-cols-3 gap-12 border-t border-neutral-200">
        {STEPS.map(([n, t, d]) => (
          <div key={n} data-testid={`landing-step-${n}`}>
            <span className="font-display text-5xl font-light text-neutral-300">{n}</span>
            <h3 className="text-lg font-medium mt-4">{t}</h3>
            <p className="text-neutral-500 mt-2 leading-relaxed text-sm">{d}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-neutral-200 py-10 px-6 sm:px-10 lg:px-20 flex flex-col sm:flex-row justify-between gap-4 text-xs text-neutral-400 tracking-wide">
        <span className="font-display text-lg text-neutral-900">interiorsmoodboard</span>
        <span data-testid="footer-credit">by Lussomora · made by <a href="https://prosperabuzz.com" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-neutral-900 transition-colors">prosperabuzz.com</a></span>
      </footer>
    </div>
  );
}
